import React, { useState, useRef, useEffect } from "react";
import {
	InfiniteLoader,
	List,
	AutoSizer,
	CellMeasurer,
	CellMeasurerCache
} from "react-virtualized";

import { shallowEqual, useDispatch, useSelector } from "react-redux";
import i18n from "i18n";

import newsActions from "../../redux/actions/newsActions";
import { getConvItemSelector } from "redux/selectors/cockpitSelectors";
import { Skeleton } from "@material-ui/lab";
import { InputBase, IconButton, Paper } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import ClientNewsPost from "./ClientNewsPost";
import FaibrikNewsPost from "./FaibrikNewsPost";
import SnackbarStore from "pages/Cockpit/SnackbarStore";
import lod_ from "lodash";

const PAGE_SIZE = 10;

/**
 * NewsPage component to display a list of news items
 * @returns {JSX.Element} - The rendered news page component
 */
const NewsPage = () => {
	const dispatch = useDispatch();
	const selectedAssistantID = useSelector(state => state.selectedAssistantID);

	const { userLanguage, dateFormat } = useSelector(getConvItemSelector, shallowEqual);

	const [searchValue, setSearchValue] = useState("");
	const [filterSearchValue, setFilteredSearchValue] = useState("");
	const [categories, setCategories] = useState([]);
	const [news, setNews] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoadingRows, setIsLoadingRows] = useState({});
	const [hasMore, setHasMore] = useState(true);

	const listRef = useRef(null);
	const infiniteLoaderRef = useRef(null);

	const cache = useRef(
		new CellMeasurerCache({
			fixedWidth: true,
			defaultHeight: 100
		})
	);

	const isRowLoaded = ({ index }) => !!news[index];

	const loadMoreRows = ({ startIndex, stopIndex }) => {
		// Avoid duplicate loads
		if (!selectedAssistantID) return Promise.resolve();
		if (isLoadingRows[startIndex]) return Promise.resolve();

		setIsLoadingRows(prev => ({ ...prev, [startIndex]: true }));

		const skip = startIndex;
		const limit = PAGE_SIZE;

		// Escape special characters for regex search to prevent injection regex
		const regexSearchValue = filterSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

		const lang = !lod_.isEmpty(userLanguage) ? userLanguage.toUpperCase() : "FR";
		const filter = !lod_.isEmpty(regexSearchValue)
			? {
					$or: [
						{ [`content.0.${lang}.text`]: { $regex: regexSearchValue, $options: "i" } },
						{ [`content.0.${lang}.subject`]: { $regex: regexSearchValue, $options: "i" } }
					]
				}
			: {};

		return new Promise(resolve => {
			dispatch(
				newsActions.getNews(selectedAssistantID, filter, limit, skip, res => {
					const newItems = res.news || [];
					const updatedNews = [...news];

					newItems.forEach((item, i) => {
						updatedNews[skip + i] = item;
					});

					setHasMore(updatedNews.length < res.totalCount);
					setNews(updatedNews);
					setTotalCount(res.totalCount || 0);
					setIsLoadingRows(prev => {
						const updated = { ...prev };
						delete updated[startIndex];
						return updated;
					});
					resolve();
				})
			);
		});
	};

	const rowRenderer = ({ key, index, parent, style }) => {
		const item = news[index];

		const getNewsPost = measure => {
			switch (item?.storage) {
				case "catalog":
					return (
						<FaibrikNewsPost
							index={index}
							news={item}
							onLoad={measure}
							language={userLanguage}
							dateFormat={dateFormat}
							categories={categories}
						/>
					);
				case "clusterClient":
				default:
					return (
						<ClientNewsPost
							index={index}
							news={item}
							onLoad={measure}
							language={userLanguage}
							dateFormat={dateFormat}
							categories={categories}
						/>
					);
			}
		};

		return (
			<CellMeasurer
				key={key}
				cache={cache.current}
				columnIndex={0}
				parent={parent}
				rowIndex={index}
			>
				{({ measure, registerChild }) => (
					<div
						ref={registerChild}
						style={{
							...style,
							padding: "1rem",
							boxSizing: "border-box",
							borderBottom: "1px solid #eee",
							display: "flex",
							justifyContent: "center"
						}}
					>
						{item ? (
							getNewsPost(measure)
						) : (
							<Skeleton
								variant="rect"
								width="70%"
								height={200}
								style={{
									borderRadius: "8px"
								}}
							/>
						)}
					</div>
				)}
			</CellMeasurer>
		);
	};

	useEffect(() => {
		if (lod_.isNil(selectedAssistantID)) return;
		dispatch(
			newsActions.getNewsCategories(selectedAssistantID, res => {
				setCategories(res.categories || []);
			})
		);
	}, [selectedAssistantID]);

	// search input change with debounce
	useEffect(() => {
		const timer = setTimeout(() => {
			setFilteredSearchValue(searchValue);
		}, 1500);
		return () => clearTimeout(timer);
	}, [searchValue]);

	// Reset and refetch when search or assistant changes
	useEffect(() => {
		setNews([]);
		setHasMore(true);
		setTotalCount(0);
		setIsLoadingRows({});
		cache.current.clearAll();
		if (infiniteLoaderRef.current) {
			infiniteLoaderRef.current.resetLoadMoreRowsCache(true);
		}
		if (listRef.current) {
			listRef.current.forceUpdateGrid();
		}
	}, [filterSearchValue, selectedAssistantID]);

	if (!selectedAssistantID) {
		return null;
	}

	return (
		<div
			style={{
				position: "relative"
			}}
		>
			<SnackbarStore />
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					padding: "16px 16px 8px"
				}}
			>
				<Paper
					elevation={0}
					style={{
						display: "flex",
						alignItems: "center",
						width: "100%",
						maxWidth: 560,
						borderRadius: 12,
						border: "1px solid #e0e0e0",
						padding: "2px 8px",
						transition: "box-shadow 0.2s ease, border-color 0.2s ease"
					}}
				>
					<SearchIcon style={{ color: "#9e9e9e", marginRight: 8 }} />
					<InputBase
						placeholder={i18n.t("COC.searchNews")}
						value={searchValue}
						onChange={e => setSearchValue(e.target.value)}
						style={{ flex: 1, fontSize: 14 }}
					/>
					{searchValue && (
						<IconButton size="small" onClick={() => setSearchValue("")}>
							<CloseIcon fontSize="small" />
						</IconButton>
					)}
				</Paper>
			</div>
			<div
				style={{
					height: "calc(100vh - 80px)"
				}}
			>
				<InfiniteLoader
					ref={infiniteLoaderRef}
					isRowLoaded={isRowLoaded}
					loadMoreRows={loadMoreRows}
					rowCount={hasMore ? news.length + PAGE_SIZE : totalCount}
				>
					{({ onRowsRendered, registerChild }) => (
						<AutoSizer>
							{({ height, width }) => (
								<List
									height={height}
									width={width}
									rowRenderer={rowRenderer}
									rowCount={hasMore ? news.length + PAGE_SIZE : totalCount}
									onRowsRendered={onRowsRendered}
									ref={el => {
										registerChild(el);
										listRef.current = el;
									}}
									deferredMeasurementCache={cache.current}
									rowHeight={cache.current.rowHeight}
								/>
							)}
						</AutoSizer>
					)}
				</InfiniteLoader>
			</div>
		</div>
	);
};

export default NewsPage;
